import React from 'react';
import { Lock, Users, FolderOpen } from 'lucide-react';

interface FolderSelectorProps {
  hasFolder: boolean;
  folderName?: string;
  onClick: (e: React.MouseEvent) => void;
  className?: string;
  ownerId?: string;
  currentUserId?: string;
  teamName?: string;
}

/**
 * Shared folder selector component used in both grid and list views
 * Displays folder icon (Lock for private, Users for shared) with folder name
 * In "My Recordings" cross-team view, shows team name and folder name
 */
export const FolderSelector: React.FC<FolderSelectorProps> = ({
  hasFolder,
  folderName,
  onClick,
  className = '',
  ownerId,
  currentUserId,
  teamName,
}) => {
  // Determine if file is truly private (owned by current user AND no folder)
  const isPrivate = !hasFolder && ownerId === currentUserId;

  // For shared files (not owned by current user), show "Shared" instead of folder name
  let displayText = folderName;
  if (!hasFolder) {
    displayText = isPrivate ? 'My Recordings' : 'Shared';
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 hover:text-foreground transition-colors ${className}`}
    >
      {teamName && (
        <>
          <Users className="h-3 w-3 flex-shrink-0" />
          <span className="truncate max-w-[80px]">{teamName}</span>
          {hasFolder && <span className="flex-shrink-0 mx-0.5">·</span>}
        </>
      )}
      {hasFolder && (
        <>
          <FolderOpen className="h-3 w-3 flex-shrink-0" />
          <span className="truncate max-w-[80px]">{folderName}</span>
        </>
      )}
      {!teamName && !hasFolder && (
        <>
          <Lock className="h-3 w-3 flex-shrink-0" />
          <span className="truncate max-w-[100px]">{displayText}</span>
        </>
      )}
    </button>
  );
};
